/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  cloneDeep,
  get,
  groupBy,
  has,
  isArray,
  isBoolean,
  isEmpty,
  isNil,
  keys,
  merge,
  omit,
  set,
  unset,
  update,
} from 'lodash';
import { NEW_ALA_CARTE_KEY, OLD_ALA_CARTE_KEY } from 'src/constants';
import { COD_COMPONENT_CONFIG } from 'src/constants/gcfComponentConfig/gcfCodConfig';
import {
  type IProduct,
  ColumnStyle,
  IComponent,
  IGcfProductListProductImage,
  IThemeRes,
} from 'src/interfaces';

export const transformComponent = (
  component: IComponent,
  products: IProduct[] = [],
  themes: IThemeRes[] = [],
) => {
  const transformedComponent = cloneDeep(component);

  transformedComponent.config = transformComponentConfig(
    transformedComponent.config,
    products,
  );

  if (
    transformedComponent?.config?.scaleTypeKey &&
    !transformedComponent?.theme_id
  ) {
    transformedComponent.theme = themes.find(
      (item) => item.name === transformedComponent.config.scaleTypeKey,
    );
    transformedComponent.theme_id = transformedComponent.theme?.id;

    unset(transformedComponent, 'config.scaleTypeKey');
  }

  return transformedComponent;
};

/*
 * These functions are used to transform old configs to the new configs where is needed.
 * This file should be maintained and when the old config is not available anymore, it can be removed.
 */
export const transformComponentConfig = (
  config: any,
  products: IProduct[] = [],
) => {
  const rootFields = transformRootFields(config);
  const productList = transformProductList(config, products);
  transformRootFieldToProductList(config, productList);

  return {
    ...rootFields,
    productList,
  };
};

const transformRootFields = (config: any): any => {
  const {
    lineup,
    visuals,
    comboList,
    subheader,
    productStyle,
    priceLabel,
    subProductsDisplayFormat,
    ...rest
  } = config;

  const data = { ...rest };

  // COD custom message

  if (
    config.codSettings &&
    isArray(config.codSettings.codSetting.footer.knownCustomer)
  ) {
    data.codSettings.codSetting.footer.knownCustomer = {
      linkedPayment: [
        ...config?.codSettings?.codSetting?.footer?.knownCustomer,
      ],
      unlinkedPayment: [
        {
          enabledTypeKey: '',
          image: '',
          iconAndText: {
            ...COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer
              .unknownCustomer[0].iconAndText,
            icon: '',
          },
        },
      ],
    };
  }

  if (
    config.codSettings &&
    isArray(config.codSettings.confirmSetting.footer.knownCustomer)
  ) {
    data.codSettings.confirmSetting.footer.knownCustomer = {
      linkedPayment: [
        ...config?.codSettings?.confirmSetting?.footer?.knownCustomer,
      ],
      unlinkedPayment: [
        {
          enabledTypeKey: '',
          image: '',
          iconAndText: {
            ...COD_COMPONENT_CONFIG.codSettings.confirmSetting.footer
              .unknownCustomer[0].iconAndText,
            icon: '',
          },
        },
      ],
    };
  }

  // LOV Custom  images
  if (config?.visuals?.visualsSource === 'custom') {
    data.images = config?.visuals?.items?.map((item: any) => ({
      visible: !!config?.visuals.visible,
      assetName: item.visualPath,
      style: {
        ...(item.zIndex && { css: `z-index: ${item.zIndex};` }),
        ...((item.top || config?.visuals?.offsetTop) && {
          top: item.top ?? config?.visuals?.offsetTop,
        }),
        ...((item.left || config?.visuals?.offsetLeft) && {
          left: item.left ?? config?.visuals?.offsetLeft,
        }),
        ...(item.width && { width: item.width }),
        ...(item.height && { height: item.height }),
      },
    }));
  }

  // DY Header Transformation Config
  if (!config.knownCustomer && config.header?.known) {
    config.knownCustomer = {
      header: {
        ...config.header,
        text: config.header.known,
      },
    };
  }

  if (!config.unknownCustomer && config.header?.unknown) {
    config.unknownCustomer = {
      header: {
        ...config.header,
        text: config.header.unknown,
      },
    };
  }

  return data;
};

const transformProductList = (config: any, products: IProduct[]) => {
  if (isNil(config.lineup) && !isEmpty(config.productList)) {
    if (!config.productList.config) {
      config.productList.config = {};
    }
    transformRootFieldToProductList(config, config.productList);
    if (config.productList.categoryConfig) {
      if (!config.productList.config.sizes) {
        config.productList.config.sizes = {};
      }
      keys(config.productList.categoryConfig).forEach((categoryKey) => {
        set(config, `productList.config.sizes.${categoryKey}`, {
          displayLabel:
            config.productList.categoryConfig[categoryKey].displayLabel,
          nameOverride:
            config.productList.categoryConfig[categoryKey].nameOverride,
          styles:
            config.productList.categoryConfig[categoryKey].stylesOverride
              ?.subproducts,
        });
      });

      delete config.productList.categoryConfig;
    }

    return config.productList;
  }

  const productList = { groups: {} };

  if (config.lineup) {
    processLineup(config, productList, products);
  }

  if (config.visuals && config.visuals.visible) {
    processVisuals(config.visuals, productList);
  }

  if (config.comboList) {
    processComboList(config.comboList, productList);
  }

  if (config.subheader) {
    processSubheader(config, productList);
  }

  return productList;
};

const transformRootFieldToProductList = (config: any, productList?: any) => {
  if (isEmpty(productList)) return;
  if (!productList.config) {
    productList.config = {};
  }

  if (config.priceLabel) {
    update(productList, 'config.sizeLabel', () => ({ ...config.priceLabel }));
  }

  if (config.subProductsDisplayFormat) {
    update(productList, 'config.subProductsDisplayFormat', () => ({
      ...config.subProductsDisplayFormat,
    }));
  }
  if (config.productStyle) {
    update(productList, 'config.products.styles', () => ({
      ...config.productStyle,
    }));
  }
};

const processLineup = (config: any, productList: any, products: IProduct[]) => {
  const groups = groupBy(config.lineup, 'group');
  keys(groups).forEach((key) => {
    const [groupName, groupId] = key.split('-');

    if (!productList.groups[groupId]) {
      productList.groups[groupId] = {
        order: Number(groupId),
        config: {
          columnOrder:
            config?.columnStyle === ColumnStyle.CATEGORY
              ? Number(groupId)
              : isNil(config?.columnOrder) && 1,
          groupHeader: {
            visible: false,
            text: '',
            description: {
              visible: false,
            },
          },
        },
        products: {},
        backups: {},
      };
    }

    if (groupName === 'group' || groupName === 'backup') {
      update(
        productList,
        `groups.${groupId}.${groupName === 'group' ? 'products' : 'backups'}`,
        () => transformLineupToProductList(groups[key], config, products),
      );
    }
  });
};

const processVisuals = (visuals: any, productList: any) => {
  const { items, offsetTop, offsetLeft, ...rest } = visuals;
  update(productList, 'config.visuals', () => ({ ...rest }));
};

const processComboList = (comboList: any, productList: any) => {
  if (comboList.categoryConfig)
    update(
      productList,
      'config.categoryConfig',
      () => comboList.categoryConfig,
    );

  update(productList, 'config.badges', () => ({
    groupBadge: {
      type: comboList.orderLabels.groups.orderType,
      startFrom: comboList.orderLabels.groups.orderFrom,
      style: {
        ...comboList.orderLabels.groups.orderStyle,
      },
    },
    itemBadge: {
      type: comboList.orderLabels.product.orderType,
      style: {
        ...comboList.orderLabels.product.orderStyle,
      },
    },
  }));

  comboList.groups?.forEach((group: any) => {
    const id = group.targetGroup.split('-')?.[1] || 1;
    const { description, ...restGroupHeader } = group.groupHeader || {};

    update(productList, `groups.${id}.config`, (config) => ({
      ...config,
      ...(!isNil(group?.column) && { column: group.column }),
      groupHeader: {
        type: 'richText',
        ...restGroupHeader,
        description: {
          type: 'richText',
          ...description,
        },
      },
    }));

    update(productList, `groups.${id}.config`, (config) => ({
      ...config,
      displayOrder: group.displayOrder,
      offsetTop: group.offsetTop,
      ...(group.productOrderOverride && {
        productOrderOverride: group.productOrderOverride,
      }),
    }));

    if (!get(productList, `groups.${id}.order`)) {
      set(productList, `groups.${id}.order`, Number(id));
    }
  });
};

const processSubheader = (config: any, productList: any) => {
  const subheader = config?.subheader || [];

  subheader.forEach((subheaderItem: any, subheaderIndex: number) => {
    const groupId = subheaderIndex + 1;

    update(productList, `groups.${groupId}.config`, (groupConfig) => ({
      ...groupConfig,
      ...(isNil(groupConfig?.columnOrder) && { columnOrder: 1 }),
      groupHeader: merge(groupConfig?.groupHeader || {}, subheaderItem || {}),
    }));

    update(productList, `groups.${groupId}`, (groupConfig) => ({
      ...groupConfig,
      ...(isNil(groupConfig?.order) && { order: groupId }),
    }));
  });
};

const transformLineupToProductList = (
  lineup: any[],
  config: any,
  allProducts: IProduct[],
) => {
  const products: Record<string, any> = {};
  lineup.forEach((lineupItem: any, index: number) => {
    if (isNil(lineupItem.sizes) || isEmpty(lineupItem.sizes)) {
      lineupItem = transformOldLineupToLineupWithSizes(lineupItem, allProducts);
    }

    const order = index + 1;
    const defaultSize = keys(lineupItem.sizes).sort(
      (a, b) =>
        Number(lineupItem.sizes[a].order) - Number(lineupItem.sizes[b].order),
    )?.[0];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { uid, group, sizes, productCode, ...rest } = lineupItem;

    const filteredRest = omit(rest, 'config.outageStamp');

    if (isEmpty(filteredRest?.config)) {
      unset(filteredRest, 'config');
    }

    update(products, `${order}`, () => ({
      order,
      productCode: lineupItem.productCode,
      config: {
        defaultSize,
        ...filteredRest,
      },
      sizes: transformSizes(lineupItem, config, order, defaultSize),
    }));
  });

  return products;
};

const transformOldLineupToLineupWithSizes = (
  lineupItem: any,
  allProducts: IProduct[],
) => {
  const newLineupItem = { sizes: {}, ...lineupItem };

  const matchedProduct = allProducts.find(
    (product) => product.product_code === lineupItem.productCode,
  );

  if (matchedProduct) {
    const sizes = groupBy(
      matchedProduct.productVariations,
      'productVariationSize.name',
    );

    keys(sizes).forEach((sizeKey, index) => {
      if (
        isEmpty(lineupItem.displayPricesFrom) ||
        lineupItem.displayPricesFrom?.includes(sizeKey) ||
        (sizeKey === NEW_ALA_CARTE_KEY &&
          lineupItem.displayPricesFrom?.includes(OLD_ALA_CARTE_KEY))
      ) {
        update(newLineupItem, `sizes.${sizeKey}`, () => ({
          order: index + 1,
          showLabel: lineupItem.displayLabels,
          showPrice: true,
          showFlavors: lineupItem.displayFlavor,
          showCalories: true,
          variations: {},
        }));

        const defaultVariation =
          // first priority is the default variation which is the group product
          sizes[sizeKey].find(
            (productVariation) =>
              productVariation.product_code ===
              productVariation.group_product_code,
          ) ||
          // second priority is the variation with no flavor
          sizes[sizeKey].find(
            (productVariation) =>
              productVariation?.productVariationFlavor?.name === '',
          );

        if (defaultVariation) {
          set(
            newLineupItem,
            `sizes.${sizeKey}.variations.${defaultVariation.product_code}`,
            {
              order: index + 1,
              productCode: defaultVariation.product_code,
            },
          );
        } else {
          sizes[sizeKey].forEach((productVariation, variationIndex) => {
            set(
              newLineupItem,
              `sizes.${sizeKey}.variations.${productVariation.product_code}`,
              {
                order: variationIndex + 1,
                productCode: productVariation.product_code,
              },
            );
          });
        }
      }
    });
  }

  return newLineupItem;
};

const transformSizes = (
  lineupItem: any,
  config: any,
  lineupOrder: number,
  defaultSize: string,
) => {
  let newSizes = {} as any;
  const { sizes } = lineupItem;

  keys(sizes).forEach((sizeKey) => {
    newSizes[sizeKey] = {
      order: sizes[sizeKey].order,
      config: {
        showLabel: sizes[sizeKey].showLabel,
        showPrice: sizes[sizeKey].showPrice,
        showCalories: sizes[sizeKey].showCalories,
        showFlavors: sizes[sizeKey].showFlavors,
      },
      variations: transformVariations(
        sizes[sizeKey].variations,
        config,
        lineupItem,
        lineupOrder,
        defaultSize === sizeKey,
      ),
    };
  });

  return newSizes;
};

const transformVariations = (
  variations: Record<string, any>,
  config: any,
  lineupItem: any,
  lineupOrder: number,
  isDefaultSize: boolean,
) => {
  let newVariations: Record<string | number, any> = {};

  keys(variations).forEach((variationKey, index) => {
    const order = index + 1;
    newVariations[variationKey] = {
      order,
      productCode: variations[variationKey].productCode,
      config: {
        images: transformVariationImages(
          order === 1,
          config,
          lineupItem,
          lineupOrder,
          isDefaultSize,
        ),
      },
    };
  });

  return newVariations;
};

const transformVariationImages = (
  isDefaultVariation: boolean,
  config: any,
  lineupItem: any,
  lineupOrder: number,
  isDefaultSize: boolean,
): IGcfProductListProductImage[] => {
  if (config.visuals) {
    // LOV
    if (has(config.visuals, 'visualsSource')) {
      if (
        config.visuals.visualsSource === 'lineup' &&
        isDefaultSize &&
        isDefaultVariation &&
        lineupOrder <= config.visuals.visualsLength
      ) {
        return createLineupImage();
      }

      if (
        config.visuals.visualsSource === 'selected' &&
        isDefaultSize &&
        isDefaultVariation
      ) {
        return createSelectedImage(config, lineupItem.uid);
      }
    } else {
      // combo
      if (isDefaultSize && isDefaultVariation) {
        return createComboImage(config.visuals.items, lineupItem.uid);
      }
    }
  }

  // LVG
  if (
    has(lineupItem.config, 'hasVisual') &&
    isDefaultSize &&
    isDefaultVariation
  ) {
    return createLVGLineupItemImage(lineupItem.config);
  }

  // Shelf
  if (lineupItem.config?.outageStamp && isDefaultSize && isDefaultVariation) {
    return createImage(
      true,
      lineupItem?.config?.outageStamp,
      undefined,
      lineupItem?.overrideAssetName,
    );
  }

  return createImage(
    isDefaultSize && isDefaultVariation,
    undefined,
    undefined,
    isDefaultSize && isDefaultVariation
      ? lineupItem?.overrideAssetName
      : undefined,
  );
};

const createImage = (
  imageVisible = false,
  outageStamp?: any,
  badgeVisible?: boolean,
  overrideAssetName?: string,
): IGcfProductListProductImage[] => [
  {
    visible: imageVisible,
    assetName: overrideAssetName ?? '',
    style: {
      css: '',
    },
    ...(isBoolean(badgeVisible) && {
      badge: {
        visible: badgeVisible,
        style: {
          css: '',
        },
      },
    }),
    ...(!isEmpty(outageStamp) && {
      outageStamp: {
        visible: !!outageStamp?.visible,
        style: {
          ...omit(outageStamp, 'visible'),
        },
      },
    }),
  },
];

const createLineupImage = (): IGcfProductListProductImage[] => [
  {
    visible: true,
    assetName: '',
    style: {
      css: '',
    },
    outageStamp: {
      visible: false,
    },
  },
];

const createSelectedImage = (
  config: any,
  lineupUid: any,
): IGcfProductListProductImage[] => {
  const visualItem = config?.visuals?.items.find(
    (visualItem: any) => visualItem.uid === lineupUid,
  );

  return visualItem
    ? [
        {
          visible: true,
          assetName: visualItem?.visualPath || '',
          style: {
            ...(visualItem?.zIndex && {
              css: `z-index: ${visualItem?.zIndex};`,
            }),
            ...((visualItem?.top || config?.visuals?.offsetTop) && {
              top: visualItem?.top ?? config?.visuals?.offsetTop,
            }),
            ...((visualItem?.left || config?.visuals?.offsetLeft) && {
              left: visualItem?.left ?? config?.visuals?.offsetLeft,
            }),
            ...(visualItem?.width && { width: visualItem?.width }),
            ...(visualItem?.height && { height: visualItem?.height }),
          },
          outageStamp: {
            ...visualItem?.outageStamp,
          },
        },
      ]
    : [];
};

const createComboImage = (
  visualItems: any[],
  lineupUid: any,
): IGcfProductListProductImage[] => {
  const visualItem = visualItems.find(
    (visualItem: any) => visualItem.linked === lineupUid,
  );

  if (!visualItem) return createImage();

  return visualItem?.sources?.map((source: any) => ({
    visible: source?.visible || true,
    assetName: source?.src ?? '',
    style: {
      ...(source?.zIndex && { zIndex: source?.zIndex }),
      ...(source?.top && { top: source?.top }),
      ...(source?.left && { left: source?.left }),
      ...(source?.width && { width: source?.width }),
      ...(source?.height && { height: source?.height }),
    },
    badge: {
      visible: source?.badge?.visible || false,
      style: {
        ...(source?.badge?.font && { font: source?.badge?.font }),
        ...(source?.badge?.size && { badgeSize: source?.badge?.size }),
        ...(source?.badge?.fontSize && { fontSize: source?.badge?.fontSize }),
        ...(source?.badge?.color && { fontColor: source?.badge?.color }),
        ...(source?.badge?.backgroundColor && {
          backgroundColor: source?.badge?.backgroundColor,
        }),
        ...(source?.badge?.top && { top: source?.badge?.top }),
        ...(source?.badge?.left && { left: source?.badge?.left }),
      },
    },
    outageStamp: {
      ...source?.outageStamp,
    },
  }));
};

const createLVGLineupItemImage = (
  lineupItemConfig: any,
): IGcfProductListProductImage[] => [
  {
    visible: lineupItemConfig.hasVisual,
    assetName: '',
    style: {
      css: '',
    },
    outageStamp: {
      ...lineupItemConfig.outageStamp,
    },
  },
];
